import { type GisMoment, gisNext } from "./signinFlow";

const SRC = "https://accounts.google.com/gsi/client";
const LOAD_MS = 10_000;

interface Notification {
  isNotDisplayed?: () => boolean;
  getNotDisplayedReason?: () => string;
  isSkippedMoment: () => boolean;
  getSkippedReason: () => string;
  isDismissedMoment: () => boolean;
  getDismissedReason: () => string;
}

interface GoogleId {
  initialize: (config: Record<string, unknown>) => void;
  prompt: (listener: (notification: Notification) => void) => void;
}

type GisWindow = Window & { google?: { accounts?: { id?: GoogleId } } };

let loading: Promise<GoogleId> | null = null;

function loadGis(): Promise<GoogleId> {
  const ready = () => (window as GisWindow).google?.accounts?.id;
  loading ??= new Promise<GoogleId>((resolve, reject) => {
    const found = ready();
    if (found) return resolve(found);
    const script = document.createElement("script");
    const timer = window.setTimeout(() => reject(Object.assign(new Error("gis load"), { code: "gis/load-timeout" })), LOAD_MS);
    script.src = SRC;
    script.async = true;
    script.onload = () => {
      window.clearTimeout(timer);
      const id = ready();
      if (id) resolve(id);
      else reject(Object.assign(new Error("gis load"), { code: "gis/no-api" }));
    };
    script.onerror = () => {
      window.clearTimeout(timer);
      reject(Object.assign(new Error("gis load"), { code: "gis/load-failed" }));
    };
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    loading = null;
    throw error;
  });
  return loading;
}

/** Load the Google script before the user taps, so the prompt opens at once. */
export const preloadGis = () => loadGis().catch(() => console.warn("sign-in: the Google script did not load"));

function momentOf(n: Notification): GisMoment | null {
  if (n.isNotDisplayed?.()) return { kind: "not_displayed", reason: n.getNotDisplayedReason?.() ?? "unknown" };
  if (n.isSkippedMoment()) return { kind: "skipped", reason: n.getSkippedReason() };
  if (n.isDismissedMoment()) return { kind: "dismissed", reason: n.getDismissedReason() };
  return null;
}

/** Ask Google Identity Services for an ID token in the page, with no popup and no redirect. */
export async function promptGoogle(clientId: string, hint: string | null): Promise<{ token: string } | GisMoment> {
  const id = await loadGis();
  return new Promise((resolve) => {
    let done = false;
    const finish = (result: { token: string } | GisMoment) => {
      if (done) return;
      done = true;
      resolve(result);
    };
    id.initialize({
      client_id: clientId,
      callback: (response: { credential?: string }) =>
        finish(response.credential ? { token: response.credential } : { kind: "dismissed", reason: "no_credential" }),
      auto_select: false,
      cancel_on_tap_outside: true,
      context: "signin",
      itp_support: true,
      use_fedcm_for_prompt: true,
      ...(hint ? { login_hint: hint } : {}),
    });
    id.prompt((notification) => {
      const moment = momentOf(notification);
      if (moment && gisNext(moment) !== "wait") finish(moment);
    });
  });
}
